import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../services/supabase';

// Hook for managing BLM data fetching
export const useBLMData = (searchTerm, selectedTable, selectedState, currentPage, itemsPerPage = 50, activeActivityTypes = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Memoize activity types string to prevent unnecessary re-renders
  const activityTypesString = useMemo(() => 
    activeActivityTypes.sort().join(','), 
    [activeActivityTypes]
  );

  useEffect(() => {
    // Only fetch data if there's a search term
    if (!searchTerm || searchTerm.trim() === '') {
      setData([]);
      setTotalCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        // Build Supabase query
        let query = supabase
          .from(selectedTable)
          .select('*', { count: 'exact' })
          .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

        // Searching table

        // Apply search term filter based on selected table
        if (searchTerm) {
          if (selectedTable === 'blm_facilities') {
            query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
          } else if (selectedTable === 'rec_areas') {
            query = query.or(`RecAreaName.ilike.%${searchTerm}%,RecAreaDescription.ilike.%${searchTerm}%`);
            // Applied rec_areas search filter
          }
        }

        // Apply activity type filter (only for blm_facilities)
        if (activeActivityTypes.length > 0 && selectedTable === 'blm_facilities') {
          query = query.in('facility_subtype', activeActivityTypes);
        }

        // Apply state filter based on selected table
        if (selectedState !== 'All States') {
          if (selectedTable === 'blm_facilities') {
            query = query.eq('state', selectedState);
          } else if (selectedTable === 'rec_areas') {
            query = query.eq('RecAreaState', selectedState);
          }
        }

        const { data: results, error, count } = await query;

        if (error) {
          setError(error.message || 'Failed to fetch results.');
          setData([]);
          setTotalCount(0);
        } else {
          // Transform data based on selected table
          let transformedData;
          
          if (selectedTable === 'blm_facilities') {
            transformedData = results.map((row, i) => ({
              id: row.blm_id || row.id || i + (currentPage - 1) * itemsPerPage,
              name: row.name || "Unnamed Site",
              description: (row.description || "").toString(),
              state: row.state || "",
              url: row.web_link || "",
              facility_subtype: row.facility_subtype,
              latitude: row.latitude,
              longitude: row.longitude,
              unit_name: row.unit_name,
              photo_thumb: row.photo_thumb,
              photo_link: row.photo_link,
              photo_text: row.photo_text
            }));
          } else if (selectedTable === 'rec_areas') {
            transformedData = results.map((row, i) => {
              return {
                id: row.RecAreaID,
                RecAreaID: row.RecAreaID,
                RecAreaName: row.RecAreaName,
                RecAreaDescription: row.RecAreaDescription,
                RecAreaDirections: row.RecAreaDirections,
                RecAreaReservationURL: row.RecAreaReservationURL,
                RecAreaLongitude: row.RecAreaLongitude,
                RecAreaLatitude: row.RecAreaLatitude,
                RecAreaUseFeeDescription: row.RecAreaUseFeeDescription,
                // Add mapped properties for consistency
                name: row.RecAreaName || "Unnamed Recreation Area",
                description: row.RecAreaDescription || "",
                latitude: row.RecAreaLatitude,
                longitude: row.RecAreaLongitude,
                state: row.RecAreaState || "",
                facility_subtype: row.subtype || "Recreation Area",
                unit_name: row.unit_name || ""
              };
            });
          } else {
            transformedData = results;
          }

          setData(transformedData);
          setTotalCount(count || results.length);
        }
      } catch (err) {
        setError(err.message);
        setData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchTerm, selectedTable, selectedState, currentPage, itemsPerPage, activityTypesString]);

  return { data, loading, error, totalCount };
};

// Hook for managing map state
export const useMapState = () => {
  const [bounds, setBounds] = useState(null);
  const [center, setCenter] = useState([-104.9903, 39.7392]);
  const [zoom, setZoom] = useState(5);
  const [basemap, setBasemap] = useState('osm');
  const [showStates, setShowStates] = useState(true);
  const mapInstanceRef = useRef(null);

  const handleBoundsChange = (newBounds) => {
    setBounds(newBounds);
  };

  const flyTo = (longitude, latitude, zoomLevel = 12) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo({ center: [longitude, latitude], zoom: zoomLevel });
    }
    setCenter([longitude, latitude]);
    setZoom(zoomLevel);
  };

  const setMapInstance = (instance) => {
    mapInstanceRef.current = instance;
  };

  return {
    bounds,
    center,
    zoom,
    basemap,
    showStates,
    setBasemap,
    setShowStates,
    handleBoundsChange,
    flyTo,
    setMapInstance,
    mapInstanceRef
  };
};

// Hook for managing filters
export const useFilters = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTable, setSelectedTable] = useState('blm_facilities');
  const [selectedState, setSelectedState] = useState('All States');
  const [activityTags, setActivityTags] = useState([
    { label: "Access Point", active: false },
    { label: "Airplane Landing Strip", active: false },
    { label: "BLM Ranger Station/Field Office/Contact Station", active: false },
    { label: "Boat Launch", active: false },
    { label: "Boat Ramp", active: false },
    { label: "Cabin", active: false },
    { label: "Campground", active: false },
    { label: "Campsite - Developed - Non Reservable - Fee", active: false },
    { label: "Campsite - Developed - Non Reservable - No Fee", active: false },
    { label: "Campsite - Developed - Reservable - Fee", active: false },
    { label: "Campsite - Primitive - Non Reservable - Fee", active: false },
    { label: "Campsite - Primitive - Non Reservable - No Fee", active: false },
    { label: "Campsite - Primitive - Reservable - Fee", active: false },
    { label: "Campsite - Primitive - Reservable - No Fee", active: false },
    { label: "ERMA", active: false },
    { label: "Fire Lookout", active: false },
    { label: "Interpretive Site", active: false },
    { label: "Lighthouse", active: false },
    { label: "Natural Area/Endangered Area", active: false },
    { label: "OHV Designated Area", active: false },
    { label: "Parking Area", active: false },
    { label: "Picnic Area", active: false },
    { label: "Point of Interest", active: false },
    { label: "Potable Water", active: false },
    { label: "SRMA", active: false },
    { label: "Scenic Overlook", active: false },
    { label: "Staging Area", active: false },
    { label: "Toilet", active: false },
    { label: "Trail Head", active: false },
    { label: "Visitor Center", active: false }
  ]);

  const toggleActivityTag = (index) => {
    setActivityTags(prev => 
      prev.map((tag, i) => 
        i === index ? { ...tag, active: !tag.active } : tag
      )
    );
  };

  const activeActivityTags = activityTags.filter(tag => tag.active);

  return {
    searchTerm,
    setSearchTerm,
    selectedTable,
    setSelectedTable,
    selectedState,
    setSelectedState,
    activityTags,
    toggleActivityTag,
    activeActivityTags
  };
};

// Hook for managing pagination
export const usePagination = (totalItems, itemsPerPage = 50) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const goToPage = (page) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  const goToNext = () => {
    goToPage(currentPage + 1);
  };

  const goToPrevious = () => {
    goToPage(currentPage - 1);
  };

  const resetPage = () => {
    setCurrentPage(1);
  };

  // Reset page when total items change (new search)
  useEffect(() => {
    setCurrentPage(1);
  }, [totalItems]);

  return {
    currentPage,
    totalPages,
    goToPage,
    goToNext,
    goToPrevious,
    resetPage,
    canGoNext: currentPage < totalPages,
    canGoPrevious: currentPage > 1
  };
};

// Hook for managing expanded items
export const useExpandedItems = () => {
  const [expandedIds, setExpandedIds] = useState([]);

  const toggleExpanded = (id) => {
    setExpandedIds(prev => 
      prev.includes(id) 
        ? prev.filter(existingId => existingId !== id)
        : [...prev, id]
    );
  };

  const isExpanded = (id) => expandedIds.includes(id);

  const collapseAll = () => setExpandedIds([]);

  return {
    expandedIds,
    toggleExpanded,
    isExpanded,
    collapseAll
  };
};

// Hook for managing selected items and locations
export const useSelection = () => {
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [location, setLocation] = useState(null);
  const [fieldNotes, setFieldNotes] = useState(null);
  const [selectedFieldNotesId, setSelectedFieldNotesId] = useState(null);

  const selectFacility = (facility) => {
    setSelectedFacility(facility);
    setLocation({
      name: facility.name,
      lat: facility.latitude,
      lon: facility.longitude
    });
  };

  const clearSelection = () => {
    setSelectedFacility(null);
    setLocation(null);
    setFieldNotes(null);
    setSelectedFieldNotesId(null);
  };

  return {
    selectedFacility,
    location,
    fieldNotes,
    selectedFieldNotesId,
    setSelectedFacility,
    setLocation,
    setFieldNotes,
    setSelectedFieldNotesId,
    selectFacility,
    clearSelection
  };
};